import { Skeleton } from '@mui/material';
import { Button, Icon, Text, Tooltip } from '@opengov/component-library';
import {
  IColumnFilterMDS,
  IColumnGroupColumn,
  IProjection,
  IReportDatasetLens,
  ISingleValueColumnFilter,
  RelativeFilter
} from '@opengov/reporting-types';
import React, { useState } from 'react';
import styles from './ColumnFiltersTooltip.module.scss';
import { RenderIf } from '../../../common/RenderIf';
import { TooltipLocaleText } from '../types';
import { useDateFiltersText } from '../hooks/useDateFiltersText';
import { countFilters, getColumnFiltersText } from './utils';

export interface IFiltersTooltipContent {
  totalAppliedFilters: number;
  appliedFiltersText: string;
  filtersList: string[];
}

export interface IColumnFiltersTooltip {
  dataTest?: string;
  entityFiscalYearStartMonth: number;
  dateFilter?: ISingleValueColumnFilter<IColumnGroupColumn>[];
  relativeFilter: RelativeFilter;
  filters?: IColumnFilterMDS<IProjection>[];
  lens: IReportDatasetLens;
  text: TooltipLocaleText;
  rolling?: number;
  asOfDate?: string;
}

const DateFilterTooltip: React.FC<{
  value: ISingleValueColumnFilter<IColumnGroupColumn>[] | undefined;
  relativeFilter: RelativeFilter;
  text: TooltipLocaleText;
  entityFiscalYearStartMonth: number;
  rolling?: number;
  asOfDate?: string;
}> = ({ value, relativeFilter, text, entityFiscalYearStartMonth, rolling, asOfDate }) => {
  const dateFiltersItems = useDateFiltersText(
    value,
    relativeFilter,
    text,
    entityFiscalYearStartMonth,
    rolling,
    asOfDate
  );
  return (
    <>
      {dateFiltersItems.length === 0 ? (
        <Skeleton />
      ) : (
        dateFiltersItems.map((item, index) => (
          <Text data-tooltip-type="datefilter" key={`date-filter-${index}`} as="div" size="minimum" color="white">
            {item}
          </Text>
        ))
      )}
    </>
  );
};

const FilterTooltip: React.FC<{
  value: IColumnFilterMDS<IProjection>[] | undefined;
  lens: IReportDatasetLens;
}> = ({ value, lens }) => {
  const columnFilters = getColumnFiltersText(value, lens);

  return (
    <>
      {columnFilters.map((item, index) => (
        <Text data-tooltip-type="filter" key={`current-filter-${index}`} size="minimum" color="white" as="div">
          {item}
        </Text>
      ))}
    </>
  );
};

const FilterTooltipTotal: React.FC<{
  count: number;
  text: TooltipLocaleText;
}> = ({ count, text }) => {
  const pluralCharacter = count > 1 ? 's' : '';
  const appliedFiltersText = text?.filtersTooltip.appliedFilters({
    filtersLength: count,
    pluralCharacter
  });
  return (
    <Text as="p" size="minimum" color="white">
      {appliedFiltersText}
    </Text>
  );
};

export const ColumnFiltersTooltip: React.FC<IColumnFiltersTooltip> = ({
  entityFiscalYearStartMonth,
  dateFilter,
  relativeFilter,
  filters,
  lens,
  text,
  dataTest = 'column-filters-tooltip',
  rolling,
  asOfDate
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const count = countFilters(dateFilter, filters);

  if (count === 0) {
    return null;
  }

  return (
    <Tooltip
      onOpen={() => {
        setIsOpen(true);
      }}
      onClose={() => {
        setIsOpen(false);
      }}
      placement="bottom-end"
      content={
        <RenderIf condition={isOpen}>
          <div className={styles.filtersTooltipContainer}>
            <FilterTooltipTotal count={count} text={text} />
            <RenderIf condition={dateFilter && dateFilter.length > 0}>
              <DateFilterTooltip
                value={dateFilter}
                relativeFilter={relativeFilter}
                text={text}
                entityFiscalYearStartMonth={entityFiscalYearStartMonth}
                rolling={rolling}
                asOfDate={asOfDate}
              />
            </RenderIf>
            <RenderIf condition={filters && filters.length > 0}>
              <FilterTooltip value={filters} lens={lens} />
            </RenderIf>
          </div>
        </RenderIf>
      }
    >
      <Button
        variant="naked"
        className={styles.filtersTooltipButton}
        data-test={`filters-tooltip-${dataTest}`}
        ariaLabel="show filters tooltip"
      >
        <Icon icon="utility-filter" />
      </Button>
    </Tooltip>
  );
};
